import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingSkeleton, EmptyState } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Map as MapIcon, Plus, ChevronUp, Rocket, Sparkles, Inbox, Pencil, Trash2, User as UserIcon, Tag, StickyNote,
} from "lucide-react";

/** Lane visuals — dark navy/cyan aesthetic. */
const LANE_META = {
  next_release: {
    label: "Next Release",
    subtitle: "Actively being built for the upcoming release",
    icon: Rocket,
    accent: "text-cyan-700 border-cyan-200 bg-cyan-50",
    dot: "bg-cyan-500 shadow-[0_0_0_4px_rgba(6,182,212,0.15)]",
  },
  planned: {
    label: "Planned",
    subtitle: "Approved and on the future roadmap",
    icon: Sparkles,
    accent: "text-violet-700 border-violet-200 bg-violet-50",
    dot: "bg-violet-500 shadow-[0_0_0_4px_rgba(139,92,246,0.15)]",
  },
  under_review: {
    label: "Under Review",
    subtitle: "Submitted by users — vote to help us prioritise",
    icon: Inbox,
    accent: "text-amber-700 border-amber-200 bg-amber-50",
    dot: "bg-amber-500 shadow-[0_0_0_4px_rgba(245,158,11,0.15)]",
  },
};

const STATUS_OPTIONS = [
  { value: "next_release", label: "Next Release" },
  { value: "planned", label: "Planned" },
  { value: "under_review", label: "Under Review" },
];

export default function Roadmap() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [editing, setEditing] = useState(null);      // item being edited (super admin)
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [addingAdmin, setAddingAdmin] = useState(false);

  const isSuper = Boolean(user?.is_super_admin);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/roadmap");
      setData(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalVotes = useMemo(() => {
    if (!data) return 0;
    return data.lanes.reduce((sum, l) => sum + l.items.reduce((s, i) => s + i.votes, 0), 0);
  }, [data]);

  const vote = async (item) => {
    // Optimistic UI — flip has_voted/votes locally first so the user sees the
    // button react instantly; roll back on error.
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        lanes: prev.lanes.map((l) => ({
          ...l,
          items: l.items.map((i) => i.id !== item.id ? i : {
            ...i,
            has_voted: !i.has_voted,
            votes: i.votes + (i.has_voted ? -1 : 1),
          }),
        })),
      };
    });
    try {
      await api.post(`/roadmap/${item.id}/vote`);
      await load();  // reconcile with server
    } catch (e) {
      toast.error(apiError(e) || "Vote failed — please try again");
      await load();  // snap back to server state
    }
  };

  const submit = async (payload) => {
    try {
      await api.post("/roadmap", payload);
      toast.success("Thanks! Your request is under review.");
      setSubmitOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveEdit = async (payload) => {
    try {
      if (editing?.id) {
        await api.patch(`/roadmap/${editing.id}`, payload);
        toast.success("Feature updated");
      } else {
        await api.post("/roadmap/admin/create", payload);
        toast.success("Feature added to roadmap");
      }
      setEditing(null);
      setAddingAdmin(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/roadmap/${confirmDelete.id}`);
      toast.success("Removed from roadmap");
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="roadmap-page">
      <PageHeader
        kicker="Product Roadmap"
        title="What we're building next."
        description="See what we're shipping, what's next, and what's up for community review. Vote on the ideas that matter to you, or submit your own."
        actions={
          <>
            {isSuper && (
              <Button
                variant="outline"
                onClick={() => { setEditing({}); setAddingAdmin(true); }}
                data-testid="admin-add-feature-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add feature
              </Button>
            )}
            <Button onClick={() => setSubmitOpen(true)} data-testid="submit-feature-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Request a feature
            </Button>
          </>
        }
      />

      {/* Summary strip */}
      {!loading && data && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="roadmap-summary">
          {data.lanes.map((lane) => {
            const meta = LANE_META[lane.key];
            const Icon = meta.icon;
            return (
              <div key={lane.key} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3">
                <div className={`h-8 w-8 rounded-lg border ${meta.accent} flex items-center justify-center`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10.5px] uppercase tracking-widest font-mono text-slate-500">{meta.label}</div>
                  <div className="text-lg font-semibold text-slate-900 leading-tight">{lane.items.length}</div>
                </div>
              </div>
            );
          })}
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 flex items-center justify-center">
              <ChevronUp className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-widest font-mono text-slate-500">Total votes</div>
              <div className="text-lg font-semibold text-slate-900 leading-tight">{totalVotes}</div>
            </div>
          </div>
        </div>
      )}

      {/* Lanes */}
      {loading ? (
        <LoadingSkeleton />
      ) : !data || data.total === 0 ? (
        <EmptyState
          icon={MapIcon}
          title="No roadmap items yet"
          description="Be the first to shape RiskEye's future. Submit an idea."
          cta={<Button onClick={() => setSubmitOpen(true)} data-testid="empty-submit-btn"><Plus className="h-4 w-4 mr-1.5" /> Request a feature</Button>}
        />
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="roadmap-board">
          {data.lanes.map((lane) => (
            <Lane
              key={lane.key}
              lane={lane}
              isSuper={isSuper}
              onVote={vote}
              onEdit={(item) => setEditing(item)}
              onDelete={(item) => setConfirmDelete(item)}
            />
          ))}
        </div>
      )}

      {/* User submission dialog */}
      <SubmitDialog open={submitOpen} onOpenChange={setSubmitOpen} onSubmit={submit} />

      {/* Super admin edit dialog (also handles "add") */}
      <EditDialog
        open={Boolean(editing)}
        onOpenChange={(v) => { if (!v) { setEditing(null); setAddingAdmin(false); } }}
        item={editing}
        isAdd={addingAdmin}
        onSave={saveEdit}
      />

      {/* Delete confirm */}
      <AlertDialog open={Boolean(confirmDelete)} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-feature-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this feature?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.title}" will be permanently removed from the roadmap, including all {confirmDelete?.votes ?? 0} vote(s). This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm-btn">
              Remove permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------- Lane ---------- */
function Lane({ lane, isSuper, onVote, onEdit, onDelete }) {
  const meta = LANE_META[lane.key];
  const Icon = meta.icon;
  return (
    <div className="rounded-xl border border-slate-200 bg-white/60" data-testid={`lane-${lane.key}`}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
        <div className={`h-8 w-8 rounded-lg border ${meta.accent} flex items-center justify-center`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="font-display font-semibold text-slate-900">{meta.label}</div>
            <span className="text-[10.5px] font-mono uppercase tracking-widest text-slate-400 border border-slate-200 rounded px-1.5 py-[1px]">
              {lane.items.length}
            </span>
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5 leading-snug">{meta.subtitle}</div>
        </div>
      </div>
      <div className="p-3 space-y-2.5 min-h-[120px]">
        {lane.items.length === 0 ? (
          <div className="text-[12.5px] text-slate-400 italic px-2 py-6 text-center">Nothing here yet.</div>
        ) : (
          lane.items.map((item) => (
            <FeatureCard key={item.id} item={item} isSuper={isSuper} onVote={onVote} onEdit={onEdit} onDelete={onDelete} />
          ))
        )}
      </div>
    </div>
  );
}

/* ---------- Feature card ---------- */
function FeatureCard({ item, isSuper, onVote, onEdit, onDelete }) {
  const voted = item.has_voted;
  return (
    <Card className="p-3.5 border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all" data-testid={`feature-${item.id}`}>
      <div className="flex gap-3">
        {/* Vote button */}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onVote(item); }}
          className={`shrink-0 flex flex-col items-center justify-center rounded-lg border px-2 py-1.5 min-w-[46px] transition-all cursor-pointer ${
            voted
              ? "bg-cyan-600 border-cyan-700 text-white shadow-[0_2px_10px_rgba(6,182,212,0.35)] hover:bg-cyan-700"
              : "bg-white border-slate-200 text-slate-700 hover:border-cyan-400 hover:text-cyan-700 hover:bg-cyan-50/50"
          }`}
          data-testid={`vote-btn-${item.id}`}
          aria-pressed={voted}
          title={voted ? "Click to remove your vote" : "Vote for this feature"}
        >
          <ChevronUp className="h-4 w-4 pointer-events-none" />
          <span className="text-[13px] font-semibold leading-none mt-0.5 pointer-events-none">{item.votes}</span>
        </button>
        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-semibold text-[14px] text-slate-900 leading-snug" data-testid={`feature-title-${item.id}`}>
                {item.title}
              </div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                {item.target_release && (
                  <Badge variant="outline" className="text-[10px] font-mono uppercase tracking-widest text-cyan-700 border-cyan-200 bg-cyan-50">
                    <Tag className="h-2.5 w-2.5 mr-1" /> {item.target_release}
                  </Badge>
                )}
                {item.source === "user" && (
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-widest text-slate-500">
                    <UserIcon className="h-3 w-3" /> Community
                  </span>
                )}
                {item.source === "system" && (
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-widest text-slate-500">
                    <Rocket className="h-3 w-3" /> Committed
                  </span>
                )}
              </div>
            </div>
            {isSuper && (
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(item)} data-testid={`edit-feature-${item.id}`} title="Edit">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => onDelete(item)} data-testid={`delete-feature-${item.id}`} title="Remove">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
          <p className="text-[13px] text-slate-600 leading-relaxed mt-2 whitespace-pre-wrap">{item.description}</p>
          {isSuper && item.admin_note && (
            <div className="mt-2.5 rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-2.5 py-1.5 text-[12px] flex gap-1.5">
              <StickyNote className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span className="whitespace-pre-wrap"><span className="font-semibold">Admin note:</span> {item.admin_note}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ---------- Submit dialog (any user) ---------- */
function SubmitDialog({ open, onOpenChange, onSubmit }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  useEffect(() => { if (open) { setTitle(""); setDescription(""); } }, [open]);
  const canSubmit = title.trim().length >= 3 && description.trim().length >= 10;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="submit-feature-dialog">
        <DialogHeader>
          <DialogTitle>Request a feature</DialogTitle>
          <DialogDescription>
            Tell us what you'd like to see in RiskEye. Every request goes into "Under Review" and can be upvoted by the whole community.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div>
            <Label htmlFor="feat-title">Title</Label>
            <Input id="feat-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Slack notifications for risk approvals" maxLength={120} data-testid="submit-feature-title" />
          </div>
          <div>
            <Label htmlFor="feat-desc">Description</Label>
            <Textarea id="feat-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={5} placeholder="What problem does this solve? Who benefits? Any details on how you'd expect it to work." maxLength={2000} data-testid="submit-feature-description" />
            <div className="text-[11px] text-slate-400 font-mono mt-1 text-right">{description.length}/2000</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="submit-cancel-btn">Cancel</Button>
          <Button disabled={!canSubmit} onClick={() => onSubmit({ title: title.trim(), description: description.trim() })} data-testid="submit-feature-confirm-btn">
            Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Edit dialog (super admin — also handles Add) ---------- */
function EditDialog({ open, onOpenChange, item, isAdd, onSave }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("planned");
  const [targetRelease, setTargetRelease] = useState("");
  const [adminNote, setAdminNote] = useState("");
  useEffect(() => {
    if (open) {
      setTitle(item?.title || "");
      setDescription(item?.description || "");
      setStatus(item?.status || "planned");
      setTargetRelease(item?.target_release || "");
      setAdminNote(item?.admin_note || "");
    }
  }, [open, item]);
  const canSave = title.trim().length >= 3 && description.trim().length >= 10;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-feature-dialog">
        <DialogHeader>
          <DialogTitle>{isAdd ? "Add feature to roadmap" : "Edit roadmap item"}</DialogTitle>
          <DialogDescription>
            {isAdd ? "This will appear on the public roadmap. Votes start at zero." : "Editorial changes only — votes and voters are preserved."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div>
            <Label htmlFor="edit-title">Title</Label>
            <Input id="edit-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} data-testid="edit-feature-title" />
          </div>
          <div>
            <Label htmlFor="edit-desc">Description</Label>
            <Textarea id="edit-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} maxLength={2000} data-testid="edit-feature-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="edit-feature-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-target">Target release (optional)</Label>
              <Input id="edit-target" value={targetRelease} onChange={(e) => setTargetRelease(e.target.value)} placeholder="v1.6" maxLength={40} data-testid="edit-feature-target" />
            </div>
          </div>
          <div>
            <Label htmlFor="edit-note">Admin note (super admins only)</Label>
            <Textarea id="edit-note" value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={2} placeholder="Internal context, blockers, decisions…" maxLength={1000} data-testid="edit-feature-admin-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-cancel-btn">Cancel</Button>
          <Button
            disabled={!canSave}
            onClick={() => onSave({
              title: title.trim(),
              description: description.trim(),
              status,
              target_release: targetRelease.trim(),
              admin_note: adminNote.trim(),
            })}
            data-testid="edit-feature-save-btn"
          >
            {isAdd ? "Add feature" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
